import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  overlayStyles,
  getOverlayAnimationStyles,
  getModalContainerStyles,
  modalHeaderStyles,
  modalTitleStyles,
  closeButtonStyles,
  closeButtonHoverStyles,
  modalBodyStyles,
  modalFooterStyles,
  modalAnimationCSS,
} from './Modal.styles';

// Icône X pour fermer
const CloseIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
  </svg>
);

export interface ModalProps {
  /** Contrôle la visibilité de la modal */
  open?: boolean;
  /** Version alternative pour la compatibilité Ant Design */
  visible?: boolean;
  /** Titre de la modal */
  title?: React.ReactNode;
  /** Fonction appelée lors de la fermeture (clic sur X, ESC, overlay) */
  onCancel?: () => void;
  /** Version alternative pour la compatibilité */
  onClose?: () => void;
  /** Contenu du footer. Si null, pas de footer. Si undefined, footer par défaut */
  footer?: React.ReactNode | null;
  /** Largeur de la modal */
  width?: number | string;
  /** Détruit le contenu lors de la fermeture */
  destroyOnClose?: boolean;
  /** Masque le bouton de fermeture X */
  closable?: boolean;
  /** Permet la fermeture en cliquant sur l'overlay */
  maskClosable?: boolean;
  /** Classe CSS personnalisée */
  className?: string;
  /** Styles inline personnalisés */
  style?: React.CSSProperties;
  /** Fond derrière la modale (scrim). Défaut : voile noir 45 %. */
  maskStyle?: React.CSSProperties;
  /** Styles de l'en-tête (fusionnés). */
  headerStyle?: React.CSSProperties;
  /** Styles du corps (fusionnés). */
  bodyStyle?: React.CSSProperties;
  /** Nom accessible quand il n'y a pas de titre visible. */
  ariaLabel?: string;
  /** Fonction appelée après ouverture */
  afterOpenChange?: (open: boolean) => void;
  /** Z-index personnalisé */
  zIndex?: number;
  /** Contenu de la modal */
  children?: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({
  open,
  visible, // Compatibilité Ant Design
  title,
  onCancel,
  onClose, // Compatibilité
  footer,
  width,
  destroyOnClose = false,
  closable = true,
  maskClosable = true,
  className,
  style,
  maskStyle,
  headerStyle,
  bodyStyle,
  ariaLabel,
  afterOpenChange,
  zIndex = 1000,
  children,
}) => {
  // Gère la compatibilité entre open et visible
  const isOpen = open ?? visible ?? false;
  const handleClose = onCancel ?? onClose ?? (() => {});

  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isHoveringClose, setIsHoveringClose] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Inject CSS animations
  useEffect(() => {
    const styleId = 'modal-animations';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = modalAnimationCSS;
      document.head.appendChild(style);
    }
  }, []);

  // Gestion de l'ouverture/fermeture avec animation
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
      afterOpenChange?.(true);
    } else if (shouldRender) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        afterOpenChange?.(false);
      }, 200); // Durée de l'animation de fermeture

      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender, afterOpenChange]);

  // Gestion du focus et de l'accessibilité
  useEffect(() => {
    if (isOpen && modalRef.current) {
      // Focus sur le premier élément focusable ou la modal elle-même
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstFocusable = focusableElements[0] as HTMLElement;
      if (firstFocusable) {
        firstFocusable.focus();
      } else {
        modalRef.current.focus();
      }
    }
  }, [isOpen]);

  // Gestion de la touche Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closable) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closable, handleClose]);

  // Gestion du focus trap
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const focusableElements = modalRef.current!.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement?.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement?.focus();
          e.preventDefault();
        }
      }
    };

    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  // Blocage du scroll du body
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen]);

  // Gestion du clic sur l'overlay
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && maskClosable) {
      handleClose();
    }
  };

  // Gestion du destroyOnClose
  if (!shouldRender && destroyOnClose) {
    return null;
  }

  if (!shouldRender) {
    return null;
  }

  const modalContent = (
    <div
      style={{
        ...overlayStyles,
        ...getOverlayAnimationStyles(isClosing),
        ...maskStyle,
        zIndex,
      }}
      onClick={handleOverlayClick}
    >
      <div
        ref={modalRef}
        className={`modal-container ${className || ''}`}
        style={{
          ...getModalContainerStyles(width, isClosing),
          ...style,
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
        aria-label={title ? undefined : ariaLabel}
        tabIndex={-1}
      >
        {(title || closable) && (
          <div style={{ ...modalHeaderStyles, ...headerStyle }}>
            {title && (
              <div id="modal-title" style={modalTitleStyles}>
                {title}
              </div>
            )}
            {closable && (
              <button
                onClick={handleClose}
                aria-label="Fermer"
                type="button"
                style={{
                  ...closeButtonStyles,
                  ...(isHoveringClose ? closeButtonHoverStyles : {}),
                }}
                onMouseEnter={() => setIsHoveringClose(true)}
                onMouseLeave={() => setIsHoveringClose(false)}
              >
                <CloseIcon />
              </button>
            )}
          </div>
        )}
        
        <div style={{ ...modalBodyStyles, ...bodyStyle }}>
          {children}
        </div>
        
        {footer !== null && (
          <div style={modalFooterStyles}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  // Rendu via portal pour être sûr d'être au-dessus de tout
  return ReactDOM.createPortal(modalContent, document.body);
};

export default Modal;
